import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { getFaqConfig } from '../utils/faqFirestore';

function FAQ() {
  const [openIndex, setOpenIndex] = useState(null);
  const [guestContext, setGuestContext] = useState(null); // 'day' | 'evening' | 'both' | null while loading
  const [config, setConfig] = useState(null); // { items, transport }
  const [canEdit, setCanEdit] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user?.phoneNumber) {
        setGuestContext('day');
        try {
          const c = await getFaqConfig();
          setConfig(c);
        } catch (err) {
          console.error('Error loading FAQ:', err);
        }
        return;
      }
      try {
        const invitesRef = collection(db, 'invites');
        const userSnap = await getDocs(query(invitesRef, where('phoneNumber', '==', user.phoneNumber)));
        if (userSnap.empty) {
          setGuestContext('day');
        } else {
          const role = userSnap.docs[0].data().role?.toLowerCase();
          setCanEdit(role === 'groom' || role === 'bride');
          const familyClan = userSnap.docs[0].data().familyClan;
          const clanSnap = await getDocs(query(invitesRef, where('familyClan', '==', familyClan)));
          const guests = clanSnap.docs.map(doc => doc.data());
          const hasDay = guests.some(g => !g.eveningGuest);
          const hasEvening = guests.some(g => g.eveningGuest);
          if (hasDay && hasEvening) setGuestContext('both');
          else if (hasEvening) setGuestContext('evening');
          else setGuestContext('day');
        }
        const c = await getFaqConfig();
        setConfig(c);
      } catch (err) {
        console.error('Error fetching FAQ or family:', err);
        setGuestContext('day');
        try {
          const c = await getFaqConfig();
          setConfig(c);
        } catch (e) {
          console.error('Error loading FAQ:', e);
        }
      }
    };
    fetchData();
  }, []);

  const getAnswerFor = (faq, forEvening) => {
    if (forEvening && faq.answerEvening != null && String(faq.answerEvening).trim()) return faq.answerEvening;
    return faq.answer;
  };

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const answerBlockStyle = {
    fontFamily: 'var(--font-body)',
    fontSize: 'clamp(0.95rem, 2.5vw, 1.1rem)',
    color: 'var(--text-secondary)',
    lineHeight: '1.8',
    margin: '0',
    textAlign: 'left'
  };

  const labelStyle = {
    fontWeight: '600',
    color: 'var(--accent-gold)',
    marginBottom: '6px',
    fontSize: '0.9em'
  };

  const renderAnswer = (html) => (
    <div style={answerBlockStyle} dangerouslySetInnerHTML={{ __html: html || '' }} />
  );

  const allFaqs = config?.items || [];
  const showToVisible = (faq) => {
    const showTo = faq.showTo || 'both';
    const context = guestContext || 'both'; // show all while loading
    if (showTo === 'both') return true;
    if (showTo === 'day') return context === 'day' || context === 'both';
    if (showTo === 'evening') return context === 'evening' || context === 'both';
    return true;
  };
  const faqs = allFaqs.filter(showToVisible);
  const transportInfo = config?.transport || { title: 'Transport', description: '', companies: [] };

  if (config === null) {
    return (
      <div className="page-container" style={{ textAlign: 'center', color: 'var(--text-primary)', paddingTop: '40px' }}>
        Loading FAQs…
      </div>
    );
  }

  return (
    <div className="page-container">
      <div style={{ position: 'relative', marginBottom: '8px' }}>
        <h2 className="page-title" style={{ margin: 0 }}>FAQs</h2>
        {canEdit && (
          <Link
            to="/faq/edit"
            style={{
              position: 'absolute',
              right: 0,
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'var(--accent-gold)',
              textDecoration: 'underline',
              fontFamily: 'var(--font-body)',
              fontSize: '0.95rem'
            }}
          >
            Edit FAQs
          </Link>
        )}
      </div>
      <p className="page-subtitle" style={{ marginBottom: '40px' }}>
        Click on any question below to see the answer
      </p>
      {guestContext === 'evening' && (
        <p className="page-subtitle" style={{ marginTop: '-24px', marginBottom: '32px', fontStyle: 'italic' }}>
          You're joining us for the evening — here's the info that applies to you.
        </p>
      )}
      <div style={{
        maxWidth: '900px',
        margin: '0 auto',
        padding: '0 20px'
      }}>
        {faqs.map((faq, index) => {
          const hasDifferentEvening = faq.answerEvening != null && String(faq.answerEvening).trim();
          const dayAnswer = faq.answer;
          const eveningAnswer = getAnswerFor(faq, true);

          let content;
          if (guestContext === 'both' && hasDifferentEvening) {
            content = (
              <>
                <div style={{ marginBottom: '16px' }}>
                  <div style={labelStyle}>Day guests</div>
                  {renderAnswer(dayAnswer)}
                </div>
                <div>
                  <div style={labelStyle}>Evening guests</div>
                  {renderAnswer(eveningAnswer)}
                </div>
              </>
            );
          } else if (guestContext === 'evening') {
            content = renderAnswer(eveningAnswer);
          } else {
            content = renderAnswer(dayAnswer);
          }

          return (
            <div
              key={index}
              className="card"
              style={{
                marginBottom: '16px',
                padding: '0',
                overflow: 'hidden',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
              onClick={() => toggleFAQ(index)}
            >
              <div style={{
                padding: '20px 24px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                backgroundColor: openIndex === index ? 'rgba(255, 255, 255, 0.12)' : 'transparent',
                transition: 'background-color 0.3s ease'
              }}>
                <h3 style={{
                  fontFamily: 'var(--font-heading)',
                  fontSize: 'clamp(1.1rem, 2.8vw, 1.4rem)',
                  color: 'var(--text-primary)',
                  margin: '0',
                  fontWeight: '600',
                  textTransform: 'none',
                  letterSpacing: '0.3px',
                  flex: '1',
                  textAlign: 'left',
                  paddingRight: '20px'
                }}>
                  {faq.question}
                </h3>
                <div style={{
                  fontSize: '1.5rem',
                  color: '#fff',
                  fontWeight: 'bold',
                  transition: 'transform 0.3s ease',
                  transform: openIndex === index ? 'rotate(45deg)' : 'rotate(0deg)',
                  flexShrink: '0',
                  width: '24px',
                  height: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  lineHeight: '1'
                }}>
                  +
                </div>
              </div>
              {openIndex === index && (
                <div style={{
                  padding: '0 24px 24px 24px',
                  animation: 'fadeIn 0.3s ease'
                }}>
                  {content}
                </div>
              )}
            </div>
          );
        })}

        {transportInfo.title && (
          <div
            className="card"
            style={{
              marginTop: '40px',
              padding: '30px',
              textAlign: 'center'
            }}
          >
            <h3 style={{
              fontFamily: 'var(--font-heading)',
              fontSize: 'clamp(1.3rem, 3.2vw, 1.6rem)',
              color: 'var(--text-primary)',
              marginBottom: '15px',
              fontWeight: '600',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              {transportInfo.title}
            </h3>
            {transportInfo.description && (
              <p style={{
                fontFamily: 'var(--font-body)',
                fontSize: 'clamp(0.95rem, 2.5vw, 1.1rem)',
                color: 'var(--text-secondary)',
                lineHeight: '1.8',
                margin: '0 0 20px 0'
              }}>
                {transportInfo.description}
              </p>
            )}
            {Array.isArray(transportInfo.companies) && transportInfo.companies.length > 0 && (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                alignItems: 'center'
              }}>
                {transportInfo.companies.map((company, idx) => (
                  <div
                    key={idx}
                    style={{
                      fontFamily: 'var(--font-body)',
                      fontSize: 'clamp(1rem, 2.5vw, 1.15rem)',
                      color: 'var(--text-secondary)',
                      padding: '8px 0'
                    }}
                  >
                    <strong style={{ color: 'var(--text-primary)', marginRight: '8px' }}>{company.name}</strong>
                    <span style={{ color: 'var(--accent-gold)' }}>–</span>
                    <span style={{ marginLeft: '8px' }}>{company.phone}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
}

export default FAQ;
